// Evidence stays in the existing per-account snapshot JSON. An older writer
// replacing that JSON drops the proof and therefore fails closed automatically.
const dayMs = 86400000
const valid = v => (typeof v === 'number' || typeof v === 'string') && String(v).trim() !== '' && Number.isFinite(Number(v))
const cents = v => valid(v) ? Math.round(Number(v) * 100) : null
const dateKey = v => {
  if (typeof v !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return false
  const time = Date.parse(`${v}T00:00:00Z`)
  return Number.isFinite(time) && new Date(time).toISOString().slice(0,10) === v
}

export function makeBalanceVerification(source, date, equity, cash, now = new Date()) {
  const b = source?.body?.currentBalances
  const time = Date.parse(source?.ingested_at)
  if (!dateKey(date) || !Number.isFinite(time) || time > now.getTime()
    || source?.broker_id !== 'schwab' || source?.payload_type !== 'account'
    || source?.source_class !== 'api' || source?.environment !== 'live'
    || !source?.payload_id || !source?.sha256 || !source?.body?.accountNumber
    || cents(equity) === null || cents(cash) === null
    || cents(b?.liquidationValue) !== cents(equity) || cents(b?.cashBalance) !== cents(cash)) return null
  const observedDate = new Date(time).toISOString().slice(0,10)
  if (observedDate > date) return null
  return {
    version: 1, status: observedDate === date ? 'source_backed' : 'carried',
    snapshot_date: date, account_number: source.body.accountNumber,
    equity_cents: cents(equity), cash_cents: cents(cash),
    observed_at: new Date(time).toISOString(), source_payload_id: source.payload_id,
    source_sha256: source.sha256,
  }
}

function accountStatus(account, date, now) {
  const proof = account?.balance_verification
  const time = Date.parse(proof?.observed_at)
  if (proof?.version !== 1 || proof.snapshot_date !== date || proof.account_number !== account.account_number
    || cents(account.equity) === null || cents(account.cash) === null
    || proof.equity_cents !== cents(account.equity) || proof.cash_cents !== cents(account.cash)
    || !proof.source_payload_id || !proof.source_sha256 || !Number.isFinite(time) || time > now.getTime()) return 'unverified'
  const sourceDate = new Date(time).toISOString().slice(0,10)
  if (proof.status === 'source_backed' && sourceDate === date) return 'verified'
  if (proof.status === 'carried' && sourceDate < date) return 'carried'
  return 'unverified'
}

export function buildBalanceCoverage(snapshots = [], { selectedAccounts = [], start = '', end = '', now = new Date(), error = null } = {}) {
  const today = now.toISOString().slice(0,10)
  const map = new Map(), duplicates = new Set()
  for (const row of snapshots) {
    if (!dateKey(row?.snapshot_date) || row.snapshot_date > today) continue
    if (map.has(row.snapshot_date)) duplicates.add(row.snapshot_date)
    map.set(row.snapshot_date,row)
  }
  const dates = [...map.keys()].sort()
  const from = dateKey(start) ? start : dates[0]
  const to = dateKey(end) ? (end > today ? today : end) : dates.at(-1)
  const rows = []
  if (from && to && from <= to && (Date.parse(to)-Date.parse(from))/dayMs <= 20000) {
    for (let t=Date.parse(from); t<=Date.parse(to); t+=dayMs) {
      const date = new Date(t).toISOString().slice(0,10), row = map.get(date)
      if (!row) { rows.push({date,status:'missing',equity:null,verifiedEquity:null}); continue }
      const all = Array.isArray(row.account_snapshots) ? row.account_snapshots : []
      const accounts = selectedAccounts.length ? all.filter(a=>selectedAccounts.includes(a.account_number)) : all
      const unique = new Set(accounts.map(a=>a.account_number))
      const totalsValid = all.length && new Set(all.map(a=>a.account_number)).size === all.length
        && all.every(a=>a.account_number && cents(a.equity)!==null && cents(a.cash)!==null)
        && cents(row.total_equity) === all.reduce((sum,a)=>sum+cents(a.equity),0)
        && cents(row.total_cash) === all.reduce((sum,a)=>sum+cents(a.cash),0)
      const complete = accounts.length > 0 && unique.size===accounts.length
        && (!selectedAccounts.length || selectedAccounts.every(a=>unique.has(a)))
      const statuses = accounts.map(a=>accountStatus(a,date,now))
      const status = error || duplicates.has(date) || !totalsValid || !complete || statuses.includes('unverified')
        ? 'unverified' : statuses.includes('carried') ? 'carried' : 'verified'
      const equity = selectedAccounts.length
        ? complete && accounts.every(a=>valid(a.equity)) ? accounts.reduce((s,a)=>s+Number(a.equity),0) : null
        : valid(row.total_equity) ? Number(row.total_equity) : null
      rows.push({ date, equity, status, verifiedEquity: status==='verified' ? equity : null,
        observedAt: accounts.map(a=>a.balance_verification?.observed_at).filter(Boolean).sort().at(-1) || null })
    }
  }
  const counts = {verified:0,unverified:0,missing:0,carried:0}
  rows.forEach(r=>counts[r.status]++)
  const incomplete = Boolean(error) || !rows.length || counts.unverified>0 || counts.missing>0 || counts.carried>0
  // Verified valuations alone are not cashflow-adjusted investment returns.
  // Never revive the old balance-ratio return/capture path merely because gaps close.
  const reason = error ? 'Balance history could not be loaded. Performance is unavailable.'
    : !rows.length ? 'No balance history is available for this period.'
    : incomplete ? 'This period includes unverified, missing or carried balances. Performance is unavailable across these gaps.'
    : 'Balances are source-backed. Investment performance remains unavailable until deposits and withdrawals are accounted for.'
  return { rows, counts, incomplete, performanceAvailable:false, reason }
}
